const configElement = document.getElementById('BuiochGiftConfig');
const config = configElement ? JSON.parse(configElement.textContent) : null;

if (config?.cardVariantId) {
  const { GiftCart, rebaseRequests } = await import(config.engineUrl || './buioch-gift-engine.js');
  config.root = `${config.root || '/'}`.replace(/\/?$/, '/');
  const engine = new GiftCart(config);
  const strings = config.strings;
  let busy = false;
  let navigating = false;
  let locked = [];
  let queue = Promise.resolve();

  const properties = value => Array.isArray(value) ? Object.fromEntries(value) : (value || {});
  const normalizeCart = cart => ({ ...cart, items: cart.items.map(item => ({ ...item, properties: properties(item.properties) })) });
  // Shopify HTML-escapes Liquid line properties, while Ajax returns the original
  // strings. Decode exactly once at the Liquid boundary, never on Ajax data.
  const liquidCart = cart => {
    const decoder = document.createElement('textarea');
    return normalizeCart({ ...cart, items: cart.items.map(item => ({
      ...item,
      properties: Object.fromEntries(Object.entries(properties(item.properties)).map(([key, value]) => {
        if (typeof value !== 'string') return [key, value];
        // Keep literal markup inert even if a value contains closing tags.
        decoder.innerHTML = value.replace(/</g, '&lt;').replace(/>/g, '&gt;');
        return [key, decoder.value];
      }))
    })) });
  };
  const editor = () => document.querySelector('buioch-gift-messages');
  const managed = item => String(item.variant_id) === String(config.cardVariantId) && ['order', 'hamper'].includes(item.properties?._buioch_card_scope);
  // Liquid and Ajax can serialize the same lines and properties in different orders.
  const snapshot = cart => JSON.stringify(cart.items.map(item => [
    String(item.key), Number(item.quantity),
    Object.entries(properties(item.properties)).sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0),
    item.parent_relationship?.parent_key ? String(item.parent_relationship.parent_key) : null
  ]).sort(([left], [right]) => left < right ? -1 : left > right ? 1 : 0));
  const savedRequests = cart => cart.items.filter(managed).map(item => ({
    scope: item.properties._buioch_card_scope,
    parentKey: item.parent_relationship?.parent_key,
    unit: Number(item.properties._buioch_card_unit || 1),
    message: item.properties['Gift message'] || ''
  }));

  function lock(value) {
    busy = value;
    document.documentElement.toggleAttribute('data-buioch-gift-busy', value);
    if (value) {
      locked = [...document.querySelectorAll('cart-drawer button, cart-drawer input, cart-drawer textarea, [data-buioch-cart-form] button, [data-buioch-cart-form] input, [data-buioch-cart-form] textarea, product-form button[type="submit"], form[action$="/cart/add"] button[type="submit"], .quick-add-wrapper button')].filter(el => !el.disabled);
      locked.forEach(el => { el.disabled = true; });
    } else {
      locked.forEach(el => { if (el.isConnected) el.disabled = false; });
      locked = [];
      editor()?.updateAvailability();
    }
    editor()?.setAttribute('aria-busy', String(value));
  }

  function perform(action) {
    const task = queue.then(async () => {
      lock(true);
      try { return await action(); } finally { lock(false); }
    });
    queue = task.catch(() => {});
    return task;
  }

  function report(error) {
    console.warn('Gift message save failed', error.code || error.name, error.cause?.message || error.message);
    const target = editor();
    if (target) {
      target.showError(strings.failure);
      return;
    }
    const drawer = document.querySelector('cart-drawer');
    let alert = drawer?.querySelector('[data-buioch-gift-alert]');
    if (drawer && !alert) {
      alert = document.createElement('p');
      alert.dataset.buiochGiftAlert = '';
      alert.className = 'buioch-gifts__error';
      alert.setAttribute('role', 'alert');
      drawer.querySelector('.global-drawer__footer')?.prepend(alert);
    }
    if (alert) alert.textContent = strings.failure;
  }

  async function post(endpoint, payload) {
    const response = await fetch(`${config.root}cart/${endpoint}.js`, {
      method: 'POST', credentials: 'same-origin',
      signal: globalThis.AbortSignal?.timeout?.(15000),
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload)
    });
    const result = await response.json();
    if (!response.ok || result.status >= 400 || result.errors) throw new Error('Cart update failed');
    return result;
  }

  async function refreshDrawer(cart) {
    const response = await fetch(`${config.root}?sections=cart-drawer`, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error('Cart refresh failed');
    const sections = await response.json();
    if (!sections['cart-drawer']) throw new Error('Cart refresh failed');
    window.eventBus?.emit('update:cart:drawer', { sections, buiochGifts: true });
    window.wetheme?.updateCartCount(cart);
  }

  function reloadCart(status) {
    if (navigating) return;
    if (status) {
      try { sessionStorage.setItem('buioch-gift-status', status); } catch {}
    }
    navigating = true;
    const destination = new URL(location.href);
    destination.pathname = `${config.root}cart`;
    destination.hash = 'buioch-gift-messages';
    if (destination.pathname === location.pathname) {
      history.replaceState(null, '', destination.href);
      location.reload();
    } else location.assign(destination.href);
  }

  function fitRequests(requests, before, after) {
    return rebaseRequests(requests, before, after, { allowRemoved: true }).filter(request => {
      if (request.scope === 'order') return after.items.some(item => !managed(item) && item.requires_shipping !== false);
      const parent = after.items.find(item => item.key === request.parentKey);
      return parent && request.unit <= parent.quantity;
    });
  }

  async function saveCart(form, checkout, removeKey) {
    const view = editor();
    const reviewFirst = !!view?.requiresReview;
    const edits = form ? [...form.querySelectorAll('[name="updates[]"][data-gift-line-key]')].map(input => ({ key: input.dataset.giftLineKey, quantity: Number(input.value) })) : [];
    const quantities = new Map(edits.map(edit => [edit.key, edit.quantity]));
    if (removeKey) quantities.set(removeKey, 0);
    const requested = view?.requests(quantities);
    if (view && requested === null) return;
    const noteScope = form?.id === 'CartDrawer-Form' ? form.closest('cart-drawer') : form;
    const note = noteScope?.querySelector('[name="note"]')?.value;
    const attributes = {};
    noteScope?.querySelectorAll('[name^="attributes["]').forEach(input => {
      const match = input.name.match(/^attributes\[(.+)\]$/);
      if (match) attributes[match[1]] = input.value;
    });
    await perform(async () => {
      view?.setStatus(strings.saving);
      let cart = await engine.read();
      let requests = view ? rebaseRequests(requested, view.cart, cart, { allowRemoved: true }) : savedRequests(cart);
      const initial = cart;
      if (removeKey) edits.push({ key: removeKey, quantity: 0 });
      for (const edit of edits) {
        const original = (view?.cart || initial).items.find(item => item.key === edit.key);
        if (!original || managed(original)) continue;
        const rebased = rebaseRequests([{ scope: 'hamper', parentKey: original.key, unit: 1, message: 'quantity' }], view?.cart || initial, cart, { allowRemoved: true });
        if (!rebased.length) continue;
        const item = cart.items.find(line => line.key === rebased[0].parentKey);
        const quantity = removeKey === edit.key ? 0 : edit.quantity;
        if (!Number.isInteger(quantity) || quantity < 0) throw new Error('Invalid quantity');
        if (item.quantity !== quantity) {
          const previous = cart;
          await post('change', { id: item.key, quantity });
          cart = await engine.read();
          requests = fitRequests(requests, previous, cart);
        }
      }
      const metadata = {};
      if (note !== undefined) metadata.note = note;
      if (Object.keys(attributes).length) metadata.attributes = attributes;
      if (Object.keys(metadata).length) {
        await post('update', metadata);
        const previous = cart;
        cart = await engine.read();
        requests = fitRequests(requests, previous, cart);
      }
      const result = await engine.save(requests);
      view?.accept(result);
      if (checkout && !reviewFirst) {
        navigating = true;
        location.assign(`${config.root}checkout`);
      } else reloadCart(strings.saved);
    });
  }

  class BuiochGiftMessages extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.cart = liquidCart(JSON.parse(this.querySelector('[data-gift-cart]').textContent));
      this.fields = this.querySelector('[data-gift-fields]');
      if (!config.cardAvailable) {
        const notice = document.createElement('p');
        notice.className = 'buioch-gifts__notice';
        notice.textContent = strings.unavailable;
        this.fields.before(notice);
      }
      this.status = this.querySelector('[data-gift-status]');
      this.error = this.querySelector('[data-gift-error]');
      this.dirty = false;
      this.requiresReview = false;
      this.render();
      this.querySelector('[data-gift-save]')?.addEventListener('click', () => {
        if (!busy) saveCart(this.closest('form'), false).catch(report);
      });
      this.addEventListener('input', () => {
        this.dirty = true;
        this.error.textContent = '';
        this.setStatus(strings.unsaved);
        this.updateAvailability();
      });
      try {
        const status = sessionStorage.getItem('buioch-gift-status');
        if (status) { this.setStatus(status); sessionStorage.removeItem('buioch-gift-status'); }
      } catch {}
    }

    render() {
      this.fields.replaceChildren();
      this.slots = [];
      if (this.cart.items.some(item => !managed(item) && item.requires_shipping !== false)) {
        this.addSlot({ scope: 'order', unit: 1 }, strings.orderTitle, strings.orderHint);
      }
      this.cart.items.filter(item => config.hamperProductIds.includes(String(item.product_id))).forEach(item => {
        for (let unit = 1; unit <= item.quantity; unit++) {
          const title = strings.hamperTitle.replace('__TITLE__', item.product_title || item.title).replace('__NUMBER__', unit);
          this.addSlot({ scope: 'hamper', parentKey: item.key, unit }, title, '');
        }
      });
      this.updateAvailability();
    }

    addSlot(request, title, hint) {
      const card = this.cart.items.find(item => managed(item) && item.properties._buioch_card_scope === request.scope && (request.scope === 'order' || (item.parent_relationship?.parent_key === request.parentKey && Number(item.properties._buioch_card_unit) === request.unit)));
      const field = document.createElement('fieldset');
      field.className = 'buioch-gifts__field';
      field.dataset.giftScope = request.scope;
      const legend = document.createElement('legend');
      legend.textContent = title;
      field.append(legend);
      if (hint) {
        const description = document.createElement('p');
        description.className = 'buioch-gifts__hint';
        description.textContent = hint;
        field.append(description);
      }
      const toggleLabel = document.createElement('label');
      toggleLabel.className = 'buioch-gifts__toggle';
      const checkbox = document.createElement('input');
      checkbox.type = 'checkbox';
      checkbox.checked = !!card;
      const toggleText = document.createElement('span');
      toggleText.textContent = strings.addCard;
      toggleLabel.append(checkbox, toggleText);
      field.append(toggleLabel);
      const content = document.createElement('div');
      content.className = 'buioch-gifts__message';
      const id = `gift-message-${this.slots.length}`;
      const label = document.createElement('label');
      label.htmlFor = id;
      label.textContent = strings.messageLabel;
      const input = document.createElement('textarea');
      input.id = id;
      input.rows = 4;
      input.placeholder = strings.placeholder;
      input.value = card?.properties['Gift message'] || '';
      input.autocomplete = 'off';
      input.setAttribute('aria-describedby', `${id}-count`);
      const count = document.createElement('p');
      count.id = `${id}-count`;
      count.className = 'buioch-gifts__count';
      content.append(label, input, count);
      field.append(content);
      checkbox.addEventListener('change', () => {
        this.dirty = true;
        this.setStatus(strings.unsaved);
        this.updateAvailability();
        if (checkbox.checked) input.focus();
      });
      this.slots.push({ request, field, checkbox, input, content, count, saved: !!card });
      this.fields.append(field);
    }

    updateAvailability() {
      if (this.requiresReview) {
        document.querySelectorAll('[data-buioch-cart-form] [name="checkout"], cart-drawer [name="checkout"]').forEach(button => { button.disabled = true; });
      }
      for (const slot of this.slots || []) {
        slot.content.hidden = !slot.checkbox.checked;
        slot.checkbox.disabled = busy || (!config.cardAvailable && !slot.saved);
        slot.input.disabled = busy || !slot.checkbox.checked;
        const count = [...slot.input.value].length;
        slot.count.textContent = strings.chars.replace('__COUNT__', count).replace('__LIMIT__', config.messageLimit);
        slot.input.setAttribute('aria-invalid', String(count > config.messageLimit));
      }
    }

    requests(quantities = new Map()) {
      const result = [];
      for (const slot of this.slots) {
        if (!slot.checkbox.checked) continue;
        if (slot.request.scope === 'hamper' && quantities.has(slot.request.parentKey) && slot.request.unit > quantities.get(slot.request.parentKey)) continue;
        if (slot.request.scope === 'order' && !this.cart.items.some(item => !managed(item) && item.requires_shipping !== false && (quantities.get(item.key) ?? item.quantity) > 0)) continue;
        const message = slot.input.value.trim();
        if (!message || [...message].length > config.messageLimit) {
          this.showError(!message ? strings.empty : strings.chars.replace('__COUNT__', [...message].length).replace('__LIMIT__', config.messageLimit));
          slot.input.setAttribute('aria-invalid', 'true');
          slot.input.focus();
          return null;
        }
        result.push({ ...slot.request, message });
      }
      return result;
    }

    setStatus(message) { if (this.status) this.status.textContent = message; }
    showError(message) { this.setStatus(''); this.error.textContent = message; this.error.hidden = false; }
    accept(cart) { this.cart = normalizeCart(cart); this.dirty = false; }

    hydrate(cart) {
      // Keep quantity inputs and remove links bound to the same merchandise when
      // an authoritative cart read has newer keys or quantities than the HTML.
      const previous = this.cart;
      // Prices and newly added rows still need a fresh server render before checkout.
      this.requiresReview = true;
      const bindings = new Map(previous.items.filter(item => !managed(item)).map(item => {
        const request = rebaseRequests([{ scope: 'hamper', parentKey: item.key, unit: 1 }], previous, cart, { allowRemoved: true })[0];
        return [item.key, request ? cart.items.find(line => line.key === request.parentKey) : null];
      }));
      this.closest('form')?.querySelectorAll('[data-gift-line-key]').forEach(element => {
        if (!bindings.has(element.dataset.giftLineKey)) return;
        const item = bindings.get(element.dataset.giftLineKey);
        if (!item) {
          element.closest('.cart__row')?.remove();
          element.removeAttribute('data-gift-line-key');
          return;
        }
        element.dataset.giftLineKey = item.key;
        if (element.matches('[name="updates[]"]')) {
          element.value = item.quantity;
          element.setAttribute('value', item.quantity);
        }
      });
      this.accept(cart);
      this.render();
      this.setStatus(strings.changed);
    }
  }

  customElements.define('buioch-gift-messages', BuiochGiftMessages);

  window.BuiochGifts = {
    async changeQuantity(key, quantity, control) {
      if (busy || !key) return;
      try {
        const view = editor();
        if (view) {
          const form = view.closest('form');
          const input = [...form.querySelectorAll('[name="updates[]"][data-gift-line-key]')].find(element => element.dataset.giftLineKey === key);
          if (!input) throw new Error('Cart changed');
          input.value = quantity;
          document.querySelector('cart-drawer')?.closeDrawer?.();
          await saveCart(form, false, quantity === 0 ? key : undefined);
          return;
        }
        await perform(async () => {
          const cart = await engine.read();
          const item = cart.items.find(line => line.key === key);
          if (!item || managed(item)) throw new Error('Cart changed');
          if (!Number.isInteger(quantity) || quantity < 0) throw new Error('Invalid quantity');
          await post('change', { id: key, quantity });
          if (control?.hasGiftWrap) {
            const current = await engine.read();
            const wrapping = current.items.find(line => line.variant_id === control.giftWrapVariantId);
            if (wrapping) await post('change', { id: wrapping.key, quantity: Math.max(0, wrapping.quantity + quantity - item.quantity) });
          }
          const result = await engine.reconcile();
          if (editor()) reloadCart(strings.quantityNotice);
          else await refreshDrawer(result);
        });
      } catch (error) {
        if (control?.input) control.input.value = control.input.getAttribute('value');
        report(error);
      }
    }
  };

  document.addEventListener('submit', event => {
    const form = event.target;
    if (!form.matches('[data-buioch-cart-form], #CartDrawer-Form')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    const checkout = event.submitter?.name === 'checkout';
    saveCart(form, checkout).catch(report);
  }, true);

  document.addEventListener('click', event => {
    const giftLink = event.target.closest('.buioch-gift-link a, .buioch-gift-edit');
    if (giftLink && editor()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (busy) return;
      document.querySelector('cart-drawer')?.closeDrawer?.();
      editor().scrollIntoView({ behavior: 'smooth', block: 'start' });
      const heading = editor().querySelector('h2');
      heading.tabIndex = -1;
      heading.focus({ preventScroll: true });
      return;
    }
    const remove = event.target.closest('[data-buioch-cart-form] .cart__remove');
    if (!remove) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (busy) return;
    const key = remove.closest('[data-gift-line-key]')?.dataset.giftLineKey;
    if (key) saveCart(remove.closest('form'), false, key).catch(report);
  }, true);

  window.addEventListener('beforeunload', event => {
    if (!navigating && (busy || editor()?.dirty)) { event.preventDefault(); event.returnValue = ''; }
  });

  let listening = false;
  function listen() {
    if (listening || !window.eventBus) return;
    listening = true;
    window.eventBus.on('update:cart:drawer', event => {
      editor()?.updateAvailability();
      if (event?.buiochGifts || busy) return;
      if (editor()?.dirty) { editor().setStatus(strings.changed); return; }
      perform(async () => {
        const before = await engine.read();
        const after = await engine.reconcile(before);
        if (editor() && snapshot(editor().cart) !== snapshot(after)) reloadCart(strings.changed);
        else if (snapshot(before) !== snapshot(after)) await refreshDrawer(after);
      }).catch(report);
    });
  }
  listen();
  window.addEventListener('load', listen, { once: true });
  if (editor()) perform(async () => {
    const before = await engine.read();
    const after = await engine.reconcile(before);
    // A read-only Liquid/Ajax difference must never cause a document reload loop.
    // Only a real cleanup mutation needs a fresh rendered cart page.
    if (snapshot(before) !== snapshot(after)) reloadCart(strings.changed);
    else if (snapshot(editor().cart) !== snapshot(after)) editor().hydrate(after);
  }).catch(report);
} else if (config) {
  const view = document.querySelector('buioch-gift-messages');
  const status = view?.querySelector('[data-gift-status]');
  if (status) status.textContent = config.strings.unavailable;
  const save = view?.querySelector('[data-gift-save]');
  if (save) save.disabled = true;
}
